/**
 * ReactiveBackground -- shell component that composites LazyGlobe and
 * ParticleCanvas into a fixed, z-minus-one decorative layer behind all
 * page content.
 *
 * Listens to:
 *   - `lukhas:agent-prompt` — toggle pulse / visibility state
 *   - `lukhas:background`   — show/hide directive
 *   - `lukhas:nav-change`   — pulse globe + re-harmonize particles on route change
 *
 * Uses `useLocation` to automatically switch to per-route particle and globe
 * skin when the URL changes (mirrors Mont-Fort's per-scene WebGL transitions).
 */
import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import LazyGlobe from './LazyGlobe'
import ParticleCanvas from './ParticleCanvas'
import domainSkinsRaw from '../../data/domainSkins.json'

/* ---------- types -------------------------------------------------- */

export interface DomainSkin {
  domainId?: string
  colorPalette?: { primary?: string; accent?: string; accent2?: string }
  particlePreset?: {
    behavior?: string
    colors?: string[]
    count?: number
    sizeRange?: [number, number]
  }
  globePresence?: 'prominent' | 'subtle' | 'hidden'
  motionOverrides?: Record<string, unknown>
}

interface RouteSkinEntry {
  label?: string
  globePresence?: 'prominent' | 'subtle' | 'hidden'
  particlePreset?: {
    behavior?: string
    colors?: string[]
    count?: number
  }
}

const routeSkins = (domainSkinsRaw as { routes?: Record<string, RouteSkinEntry> }).routes ?? {}

/* ---------- component ---------------------------------------------- */

export default function ReactiveBackground({ skin }: { skin?: DomainSkin }) {
  const location = useLocation()
  const [agentActive, setAgentActive] = useState(false)
  const [visible, setVisible] = useState(true)
  /* Brief pulse on route change — same energy as Mont-Fort's camera curve */
  const [navPulse, setNavPulse] = useState(false)
  const pulseTimer = useRef<ReturnType<typeof setTimeout>>()

  /* Merge domain-level skin with per-route overrides */
  const routeSkin = routeSkins[location.pathname]
  const resolvedGlobePresence =
    routeSkin?.globePresence ?? skin?.globePresence ?? 'prominent'
  const resolvedColors =
    routeSkin?.particlePreset?.colors ??
    skin?.particlePreset?.colors ??
    ['#ffffff', '#38bdf8', '#a78bfa']
  void (routeSkin?.particlePreset?.count ?? skin?.particlePreset?.count) // reserved for future count prop

  /* Listen for agent-prompt events */
  useEffect(() => {
    const onAgentPrompt = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.active !== undefined) setAgentActive(Boolean(detail.active))
    }
    window.addEventListener('lukhas:agent-prompt', onAgentPrompt)
    return () => window.removeEventListener('lukhas:agent-prompt', onAgentPrompt)
  }, [])

  /* Listen for background show/hide directives */
  useEffect(() => {
    const onBackground = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.payload?.active !== undefined) {
        setVisible(Boolean(detail.payload.active))
      }
    }
    window.addEventListener('lukhas:background', onBackground)
    return () => window.removeEventListener('lukhas:background', onBackground)
  }, [])

  /* Listen for nav-change events — pulse globe briefly (re-harmonize) */
  useEffect(() => {
    const onNavChange = () => {
      clearTimeout(pulseTimer.current)
      setNavPulse(true)
      pulseTimer.current = setTimeout(() => setNavPulse(false), 900)
    }
    window.addEventListener('lukhas:nav-change', onNavChange)
    return () => {
      window.removeEventListener('lukhas:nav-change', onNavChange)
      clearTimeout(pulseTimer.current)
    }
  }, [])

  /* Also react to React Router location changes directly */
  useEffect(() => {
    clearTimeout(pulseTimer.current)
    setNavPulse(true)
    pulseTimer.current = setTimeout(() => setNavPulse(false), 900)
    return () => clearTimeout(pulseTimer.current)
  }, [location.pathname])

  const globeHidden = resolvedGlobePresence === 'hidden'
  const globeSize = resolvedGlobePresence === 'prominent' ? '360px' : '240px'

  if (!visible) return null

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: -1 }}
    >
      {/* Globe -- top-left, partially off-screen; pulses on route change */}
      {!globeHidden && (
        <div
          className="absolute transition-opacity duration-700"
          style={{
            top: '-60px',
            left: '-60px',
            opacity: resolvedGlobePresence === 'prominent' ? 0.2 : 0.12,
          }}
        >
          <LazyGlobe
            width={globeSize}
            height={globeSize}
            pulse={agentActive || navPulse}
          />
        </div>
      )}

      {/* Particle ring -- bottom-right; re-harmonizes colors on route change */}
      <div className="absolute bottom-16 right-16">
        <ParticleCanvas
          active={agentActive || navPulse}
          size={120}
          ringRadius={44}
          colors={resolvedColors}
        />
      </div>
    </div>
  )
}
